import { Button, Checkbox, Modal, Radio, RadioChangeEvent, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { useState } from 'react';

import { guessResultMode } from '../constants/game';

interface SinglePlayerModalProps {
    visible: boolean;
    onModalVisibility: (isVisible: boolean) => void;
    onClickStartGame: (guessResultMode: string, noMove?: boolean) => void;
}

const modalStyles = {
    container: {
        textAlign: 'center' as const,
        padding: '8px 0',
    },
    icon: {
        fontSize: '52px',
        marginBottom: '12px',
        display: 'block',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: '8px',
        letterSpacing: '-0.5px',
    },
    subtitle: {
        fontSize: '14px',
        color: '#666',
        marginBottom: '24px',
    },
    optionsCard: {
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #e8e8e8',
        textAlign: 'left' as const,
    },
    optionsLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#8c8c8c',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '14px',
        display: 'block',
    },
    radioOption: {
        padding: '12px 16px',
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #e8e8e8',
        marginBottom: '8px',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    radioOptionSelected: {
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #fff7e6 0%, #fff1d6 100%)',
        borderRadius: '8px',
        border: '1px solid #ffd591',
        marginBottom: '8px',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    radioLabel: {
        fontSize: '15px',
        fontWeight: 500,
        color: '#1a1a1a',
    },
    hintBox: {
        background: 'linear-gradient(135deg, #fff7e6 0%, #fff1d6 100%)',
        borderRadius: '10px',
        padding: '14px 16px',
        marginTop: '16px',
        border: '1px solid #ffd591',
    },
    hintText: {
        fontSize: '13px',
        color: '#ad6800',
        lineHeight: '1.5',
        margin: 0,
    },
    advancedSection: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px dashed #e8e8e8',
    },
    advancedCheckbox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    advancedLabel: {
        fontSize: '14px',
        color: '#595959',
    },
    advancedBadge: {
        fontSize: '10px',
        fontWeight: 600,
        color: '#fff',
        background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
        padding: '2px 8px',
        borderRadius: '10px',
        marginLeft: '8px',
    },
    playButton: {
        marginTop: '24px',
        width: '100%',
        height: '52px',
        fontSize: '18px',
        fontWeight: 600,
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #c80707 0%, #a00606 100%)',
        border: 'none',
        boxShadow: '0 4px 12px rgba(200, 7, 7, 0.35)',
    },
};

export const SinglePlayerModal = ({ visible, onModalVisibility, onClickStartGame }: SinglePlayerModalProps) => {
    const [resultModeValue, setResultModeValue] = useState(guessResultMode.end);
    const [noMoveValue, setNoMoveValue] = useState(false);

    const handleCancel = () => {
        onModalVisibility(false);
    };

    const handleOnChange = (e: RadioChangeEvent) => {
        setResultModeValue(e.target.value);
    };

    const handleOnNoMoveChange = (e: CheckboxChangeEvent) => {
        setNoMoveValue(e.target.checked);
    };

    return (
        <Modal
            open={visible}
            footer={null}
            onCancel={handleCancel}
            centered
            width={440}
        >
            <div style={modalStyles.container}>
                <span style={modalStyles.icon}>🎮</span>
                <h2 style={modalStyles.title}>Hra jednoho hráče</h2>
                <p style={modalStyles.subtitle}>Prozkoumej Česko vlastním tempem</p>

                <div style={modalStyles.optionsCard}>
                    <span style={modalStyles.optionsLabel}>Režim hádání</span>

                    <Radio.Group
                        onChange={handleOnChange}
                        value={resultModeValue}
                        style={{ width: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div
                                style={resultModeValue === guessResultMode.end
                                    ? modalStyles.radioOptionSelected
                                    : modalStyles.radioOption}
                                onClick={() => handleOnChange({ target: { value: guessResultMode.end } } as RadioChangeEvent)}
                            >
                                <Radio value={guessResultMode.end}>
                                    <span style={modalStyles.radioLabel}>📍 Aktuální polohu v panoráma</span>
                                </Radio>
                            </div>
                            <div
                                style={resultModeValue === guessResultMode.start
                                    ? modalStyles.radioOptionSelected
                                    : modalStyles.radioOption}
                                onClick={() => handleOnChange({ target: { value: guessResultMode.start } } as RadioChangeEvent)}
                            >
                                <Radio value={guessResultMode.start}>
                                    <span style={modalStyles.radioLabel}>🚩 Polohu místa, kde začínám</span>
                                </Radio>
                            </div>
                        </Space>
                    </Radio.Group>

                    <div style={modalStyles.hintBox}>
                        <p style={modalStyles.hintText}>
                            {resultModeValue === guessResultMode.start ? (
                                <>
                                    <strong>GeoGuessr styl:</strong> Zjistíš, kde to sakra jsi, a pak se v mapě snažíš dohledat místo, odkud se vyráželo.
                                </>
                            ) : (
                                <>
                                    <strong>Tip:</strong> Dojeď v panoráma někam, kde už to bezpečně poznáváš. Pak označ místo s přesností na metr!
                                </>
                            )}
                        </p>
                    </div>

                    <div style={modalStyles.advancedSection}>
                        <Checkbox
                            checked={noMoveValue}
                            onChange={handleOnNoMoveChange}
                        >
                            <span style={modalStyles.advancedLabel}>
                                Zakázat pohyb v panoráma
                                <span style={modalStyles.advancedBadge}>PRO</span>
                            </span>
                        </Checkbox>
                    </div>
                </div>

                <Button
                    type="primary"
                    size="large"
                    style={modalStyles.playButton}
                    onClick={() => onClickStartGame(resultModeValue, noMoveValue)}
                >
                    🎯 Začít hrát
                </Button>
            </div>
        </Modal>
    );
};
